import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Radar } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, buttonClass, inputClass } from "../components/Bits";

const STATES = ["TX", "OK", "LA", "NM", "AR", "AZ", "CA", "CO", "FL", "GA", "NY"];

const STATUS_TONE = {
  hit: { dot: "bg-gold", text: "text-gold", label: "Signal found" },
  clear: { dot: "bg-sage", text: "text-sage", label: "No signal" },
  not_found: { dot: "bg-cream/30", text: "text-cream/45", label: "No record" },
  unavailable: { dot: "bg-terra", text: "text-terra", label: "Source unavailable" },
};

const CONFIDENCE_TONE = { High: "text-gold", Medium: "text-sage", Low: "text-cream/50" };

export default function AdminCoEmployer() {
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({
    company: params.get("company") || "",
    state: params.get("state") || "TX",
  });
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = () =>
    adminApi.get("/crm/radar/reports", { params: { limit: 15 } })
      .then(({ data }) => setHistory(data.reports)).catch(() => {});

  useEffect(() => { loadHistory(); }, []);

  const run = useCallback(async (company, state) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/crm/radar/analyze", { company, state });
      setReport(data);
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  // "Run radar" from an account row lands here with the name already filled in.
  useEffect(() => {
    const company = params.get("company");
    if (params.get("run") === "1" && company) {
      const state = params.get("state") || "TX";
      setParams({ company, state }, { replace: true });
      run(company, state);
    }
  }, [params, setParams, run]);

  const analyze = async (event) => {
    event.preventDefault();
    if (!form.company.trim()) return;
    run(form.company.trim(), form.state);
  };

  const openReport = async (id) => {
    try {
      const { data } = await adminApi.get(`/crm/radar/reports/${id}`);
      setReport(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyToCrm = async () => {
    try {
      const { data } = await adminApi.post(`/crm/radar/reports/${report.id}/apply`);
      toast.success(`Saved ${data.peo_name} onto the account`);
      setReport({ ...report, applied_to_crm: true });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-coemployer-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Co-Employer Radar</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Is this employer co-employed?
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          Six public sources, one verdict. A source that cannot be read says so plainly rather than
          guessing — so a &ldquo;High&rdquo; confidence here always means a name was found on an
          employer-side record <em>and</em> verified against the IRS Certified PEO registry.
        </p>
      </div>

      <Panel testid="panel-radar-search">
        <form onSubmit={analyze} className="flex flex-wrap items-end gap-3" data-testid="radar-form">
          <div className="min-w-[16rem] flex-1 space-y-1.5">
            <label htmlFor="radar-company"
                   className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              Target company name
            </label>
            <input id="radar-company" required value={form.company} className={inputClass}
                   placeholder="Legal entity name as filed"
                   data-testid="radar-company-input"
                   onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div className="w-32 space-y-1.5">
            <label htmlFor="radar-state"
                   className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              State
            </label>
            <select id="radar-state" value={form.state} className={inputClass}
                    data-testid="radar-state-select"
                    onChange={(e) => setForm({ ...form, state: e.target.value })}>
              {STATES.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} className={buttonClass} data-testid="radar-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}
            {busy ? "Analyzing…" : "Analyze Entity"}
          </button>
        </form>
      </Panel>

      {report ? (
        <>
          <section className="rounded-xl border border-gold/30 bg-[#10211A] p-6" data-testid="radar-summary">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
                  Executive summary
                </p>
                <h2 className="mt-3 font-serif text-3xl font-light leading-tight text-cream"
                    data-testid="radar-verdict">
                  {report.verdict}
                </h2>
                <p className="mt-2 text-sm text-cream/55">
                  {report.company} · {report.state} · run by {report.run_by}
                </p>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Status</p>
                  <p className="mt-2 text-sm text-cream" data-testid="radar-status">{report.status}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    Confidence
                  </p>
                  <p className={`mt-2 text-sm ${CONFIDENCE_TONE[report.confidence]}`}
                     data-testid="radar-confidence">
                    {report.confidence}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    Co-employer
                  </p>
                  <p className="mt-2 text-sm text-gold" data-testid="radar-peo-name">
                    {report.peo_name || "Not identified"}
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-6 max-w-4xl text-sm leading-relaxed text-cream/70"
               data-testid="radar-reasoning">
              {report.reasoning}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              {report.account_id ? (
                <Link to={`/admin/accounts/${report.account_id}`}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50 transition-colors duration-200 hover:text-gold"
                      data-testid="radar-account-link">
                  Open the CRM account →
                </Link>
              ) : null}
              {report.peo_name && report.account_id && !report.applied_to_crm ? (
                <button type="button" onClick={applyToCrm} className={buttonClass}
                        data-testid="radar-apply-button">
                  Save this PEO onto the account
                </button>
              ) : null}
              {report.applied_to_crm ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage"
                      data-testid="radar-applied-note">
                  Saved to the CRM
                </span>
              ) : null}
            </div>
          </section>

          <Panel title="Data matrix" testid="radar-matrix">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-left">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Result</th>
                    <th className="py-2">What it said</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream/10">
                  {Object.entries(report.checks || {}).map(([key, check]) => {
                    const tone = STATUS_TONE[check.status] || STATUS_TONE.not_found;
                    return (
                      <tr key={key} data-testid={`matrix-row-${key}`}>
                        <td className="py-3 pr-4 align-top text-sm text-cream/85">{check.label}</td>
                        <td className="py-3 pr-4 align-top">
                          <span className="inline-flex items-center gap-2">
                            <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                            <span className={`text-xs ${tone.text}`}>{tone.label}</span>
                          </span>
                        </td>
                        <td className="py-3 align-top text-xs leading-relaxed text-cream/60">
                          {check.summary}
                          {(check.evidence || []).length ? (
                            <ul className="mt-1.5 space-y-1">
                              {check.evidence.map((line) => (
                                <li key={line} className="text-cream/45">· {line}</li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      ) : null}

      <Panel title="Recent searches" testid="radar-history">
        <ul className="divide-y divide-cream/10">
          {history.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid={`radar-history-${item.id}`}>
              <button type="button" onClick={() => openReport(item.id)}
                      className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                {item.company} <span className="text-cream/35">· {item.state}</span>
              </button>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                {item.status} · {item.confidence}
                {item.peo_name ? ` · ${item.peo_name}` : ""}
              </span>
            </li>
          ))}
          {history.length === 0 ? (
            <li className="py-3 text-sm text-cream/40" data-testid="radar-history-empty">
              No searches yet.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
